/* eslint-disable import/extensions */
import { useDispatch, useSelector } from "react-redux";
import { useEffect, useState } from "react";
import styled from "styled-components";

import {
	ComplexFilterPanel,
	ReservationsCalendar,
	ReservationsList,
	ReservationDetailsPanel,
} from "components/organisms";
import { ReservationsSummary, PageHeader, SearchInput } from "components/molecules";

import {
	getReservations,
	getReservationsFilter,
	getReservationsList,
	getReservationsLoading,
	setReservationsFilter,
} from "state/modules/reservations";

import { SearchComplexFilter, ComplexFilterItem, ComplexFilterType } from "interfaces/filters";
import { ReportTableItemWithTransfer } from "interfaces/reports";

import { defaultReservationsDateFilter } from "utils/reservations";

import { MAIN_THEME } from "styles/colors";
import { FONT_FAMILY_BOLD, FONT_WEIGHT_BOLD } from "styles/typography";



const TabButton = styled.button<{ isActive: boolean }>`
	background: transparent;
	color: ${(props) => (props.isActive ? MAIN_THEME.SECONDARY : MAIN_THEME.TEXT_SECONDARY)};
	border: none;
	border-bottom: 2px solid ${(props) => (props.isActive ? MAIN_THEME.SECONDARY : "transparent")};
	padding: 12px 4px;
	border-radius: 0;
	cursor: pointer;
	margin-right: 24px;
	font-weight: 500;
	font-size: 16px;
	line-height: 20px;
	transition: all 0.2s ease;
	outline: none;

	&:hover {
		${(props) =>
			!props.isActive
				? `color: ${MAIN_THEME.TEXT_PRIMARY};`
				: ""}
	}

	@media (max-width: 768px) {
		font-size: 14px;
		padding: 10px 8px;
		margin-right: 16px;
	}
`;

const FiltersContainer = styled.div`
	margin-bottom: 15px;
	z-index: 10;
	position: relative;
	padding: 0 20px;
`;

const CalendarContainer = styled.div`
	padding: 0 20px;
	margin-bottom: 15px;
	/* background: #fff; */
`;

const TableContainer = styled.div`
	padding: 20px;
`;

const ReservationsSummaryContainer = styled.div`
	padding: 20px;
`;

const Tabs = styled.div`
	padding: 20px;
`;

const Header = styled.div`
	padding: 0 40px 10px;
	margin-bottom: 5px;
`;

const Container = styled.div`
	width: 100%;
	height: 100vh;
	overflow: auto;
	position: relative;
	overflow-y: auto;
	background: ${MAIN_THEME.DARK_BG};

	@media (max-width: 768px) {
		height: auto;
		min-height: 100vh;
	}
`;

const filterOptions = [
	ComplexFilterType.CHANNEL,
	ComplexFilterType.DATE,
	ComplexFilterType.PROPERTY_ID,
	ComplexFilterType.STATUS,
];

enum Tab {
	TABLE,
	CALENDAR,
}

const ReservationsPage = (): JSX.Element => {
	const [tab, setTab] = useState(Tab.TABLE);
	const [selectedReservation, setSelectedReservation] =
		useState<ReportTableItemWithTransfer | null>(null);

	const dispatch = useDispatch();

	const filters = useSelector(getReservationsFilter);
	const reservations = useSelector(getReservationsList);
	const isLoading = useSelector(getReservationsLoading);

	useEffect(() => {
		dispatch(getReservations());
	}, [dispatch]);

	const handleChangeFilter = (updatedFilter: ComplexFilterItem) => {
		const updatedFilters = filters.map((filter: ComplexFilterItem) => {
			if (filter.type === updatedFilter.type) {
				return updatedFilter;
			}

			return filter;
		});

		dispatch(setReservationsFilter(updatedFilters));
		dispatch(getReservations());
	};

	const handleCreateFilter = (filter: ComplexFilterItem) => {
		const updatedFilters = [...filters, filter];

		dispatch(setReservationsFilter(updatedFilters));
	};

	const handleDeleteFilter = (selectedfilter: ComplexFilterItem) => {
		const updatedFilters = filters.filter(
			(filter: ComplexFilterItem) => filter.type !== selectedfilter.type
		);

		dispatch(setReservationsFilter(updatedFilters));
		dispatch(getReservations());
	};

	const handleClearFilter = () => {
		const filteredFilters = filters.filter(
			(filter: ComplexFilterItem) => filter.type === ComplexFilterType.DATE
		);

		const updatedFilters = filteredFilters.map((item) => ({
			type: ComplexFilterType.DATE,
			value: {
				...defaultReservationsDateFilter,
			},
		})) as ComplexFilterItem[];

		dispatch(setReservationsFilter(updatedFilters));
		dispatch(getReservations());
	};

	const handleSelectReservation = (
		item: ReportTableItemWithTransfer | null
	) => {
		setSelectedReservation(item);
	};

	const handleCloseDetailsPanel = () => {
		setSelectedReservation(null);
	};

	const handleSearchChange = (updatedFilter: SearchComplexFilter) => {
		const filteredFilters = filters.filter(
			(filter: ComplexFilterItem) => filter.type !== ComplexFilterType.SEARCH
		);
		dispatch(setReservationsFilter([...filteredFilters, updatedFilter]));
		dispatch(getReservations());
	};

	const renderFilters = () => (
		<FiltersContainer>
			<div style={{ display: 'flex', gap: '15px', alignItems: 'center', flexWrap: 'wrap' }}>
				<SearchInput
					filterData={filters.find(f => f.type === ComplexFilterType.SEARCH) as SearchComplexFilter || { type: ComplexFilterType.SEARCH, value: "" }}
					changeFilter={handleSearchChange}
					placeholder="Search reservations..."
					background="transparent"
					border={`1px solid ${MAIN_THEME.BORDER_COLOR}`}
					width="350px"
				/>
				<ComplexFilterPanel
					filters={filters.filter(f => f.type !== ComplexFilterType.SEARCH)}
					onChange={handleChangeFilter}
					onCreate={handleCreateFilter}
					onDelete={handleDeleteFilter}
					onClear={handleClearFilter}
					options={filterOptions.filter(opt => opt !== ComplexFilterType.SEARCH)}
					hideSearch
				/>
			</div>
		</FiltersContainer>
	);

	const renderContent = () => {
		if (tab === Tab.TABLE) {
			return (
				<>
					{renderFilters()}
					<ReservationsSummaryContainer>
						<ReservationsSummary isLoading={isLoading} />
					</ReservationsSummaryContainer>
					<TableContainer>
						<ReservationsList
							showProperty
							reservations={reservations}
							isLoading={isLoading}
							onSelect={handleSelectReservation}
						/>
					</TableContainer>
				</>
			);
		}
		return (
			<CalendarContainer>
				<ReservationsCalendar />
				{/* <FullCalendar
					plugins={[dayGridPlugin]}
					initialView="dayGridMonth"
					events={[
						{
							id: 
							start: dayjs().startOf("month").toISOString(),
							end: dayjs().endOf("month").toISOString(),
							title: "Test",
							allDay: true,
							interactive: true,
						},
					]}
					// themeSystem="bootstrap"
					// views={{
					// 	dayGrid: {
					// 		// options apply to dayGridMonth, dayGridWeek, and dayGridDay views
					// 	},
					// 	timeGrid: {
					// 		// options apply to timeGridWeek and timeGridDay views
					// 	},
					// 	week: {
					// 		// options apply to dayGridWeek and timeGridWeek views
					// 	},
					// 	day: {
					// 		// options apply to dayGridDay and timeGridDay views
					// 	},
					// }}
				/> */}
			</CalendarContainer>
		);
	};

	const renderReservationsDetailsPanel = () => {
		if (selectedReservation) {
			return (
				<ReservationDetailsPanel
					close={handleCloseDetailsPanel}
					hostServiceFee={`${selectedReservation._item.host_service_fee}`}
					ownerIncome={`${selectedReservation._item.owner_income}`}
					hoteliseHelp={`${selectedReservation._item.hotelise_help}`}
					payoutPrice={`${selectedReservation._item.payout_price}`}
					currency={selectedReservation._item.currency}
					propertyName={selectedReservation.property?.name || ""}
					propertyAddress={selectedReservation.property?.address?.display || ""}
					checkIn={selectedReservation._item.start_date}
					checkOut={selectedReservation._item.end_date}
					nights={selectedReservation._item.nights}
					guestsNumber={0}
					image={selectedReservation.property?.picture || ""}
					cleaningFee={selectedReservation._item.cleaning ? `${selectedReservation._item.cleaning}` : undefined}
					guest={selectedReservation._item.guest}
				/>
			);
		}
	};

	return (
		<Container id="reservationsListContainer">
			<Header>
				<PageHeader
					title="Reservations"
					description="View and manage all reservations across properties."
				/>
			</Header>
			<Tabs style={{ padding: "0 20px 20px" }}>
				<TabButton
					onClick={() => setTab(Tab.TABLE)}
					isActive={tab === Tab.TABLE}
				>
					Reservations
				</TabButton>
				<TabButton
					onClick={() => setTab(Tab.CALENDAR)}
					isActive={tab === Tab.CALENDAR}
				>
					Calendar View
				</TabButton>
			</Tabs>
			{renderContent()}
			{renderReservationsDetailsPanel()}
		</Container>
	);
};

export default ReservationsPage;
